import DeleteThinIcon from '@assets/icons/delete-thin.svg';
import i18n from '@/utils/i18n';
import MicIcon from '@assets/icons/mic.svg';
import NotificationBellIcon from '@assets/icons/notification-bell.svg';
import React, { useEffect, useRef, useState } from 'react';
import { AddNoteReminderActionSheet } from './add-note-reminder-action-sheet';
import { BaseActionSheet } from '@/components/base-action-sheet';
import { TextInput as BaseTextInput } from 'react-native/Libraries/Components/TextInput/TextInput';
import { Button } from '@/components/button';
import { CloseButton } from '@/components/close-button';
import { createStyles } from '@/contexts/theme-context';
import {
  formatDate,
  formatDateWithDayAndMonth,
  get12HourFormat,
} from '@/utils/date';
import { MultiStyleText } from '@/components/multi-style-text';
import {
  NativeSyntheticEvent,
  Platform,
  ScrollView,
  useWindowDimensions,
  View,
} from 'react-native';
import { Position } from '@/contexts/toast-context';
import { ReminderTimestamp } from '@/hooks/use-note-reminder-form';
import { TextInput } from '@/components/text-input';
import { Typography } from '@usepepper/ui/atoms/typography';
import { TYPOGRAPHY } from '@/styles';
import { useAddNoteActivity } from '@/hooks/use-add-note-activity';
import { useLayoutContext } from '@/contexts/layout-context';
import { useSpeechRecognition } from '@/hooks/use-speech-recognition';

const NOTE_TEXT_HEIGHT_PERCENT = 0.4;
const MIN_NOTE_TEXT_HEIGHT = 32;

interface Props {
  chatUUID: string;
  onClose: () => void;
}

interface NewNoteActionSheetContentProps extends Props {
  showCloseButton?: boolean;
}

export const NewNoteActionSheetContent: React.FC<
  NewNoteActionSheetContentProps
> = ({ chatUUID, onClose, showCloseButton = false }) => {
  const buttonRef = useRef<View>(null);
  const inputRef = useRef<BaseTextInput>(null);
  const [styles, theme] = useStyles();
  const { isSmallLayout } = useLayoutContext();
  const { height: screenHeight } = useWindowDimensions();

  const [note, setNote] = useState('');
  const [isToastVisible, setIsToastVisible] = useState(false);
  const [noteReminder, setNoteReminder] = useState<
    ReminderTimestamp | undefined
  >(undefined);
  const [showReminderSheet, setShowReminderSheet] = useState(false);
  const [textInputSize, setTextInputSize] = useState(MIN_NOTE_TEXT_HEIGHT);
  const noteDate = new Date();

  const { createNote, loading, convertReminderToDate, showErrorToast } =
    useAddNoteActivity(chatUUID, note, noteReminder);

  const isMobileWeb = Platform.OS === 'web' && isSmallLayout;
  const notesRemindersEnabled = !isMobileWeb;

  useEffect(() => {
    const timer = setTimeout(() => {
      if (Platform.OS === 'web' && inputRef.current) {
        inputRef.current?.focus();
      }
    }, 1000); // arbitrary delay to allow the action sheet to open

    return () => clearTimeout(timer);
  }, []);

  const cursorPositionRef = useRef(0);
  const { canListen, isListening, startRecognition, stopRecognition } =
    useSpeechRecognition({
      cursorPositionRef,
      inputValue: note,
      setInputValue: setNote,
    });

  const closeReminder = () => {
    setShowReminderSheet(false);
  };

  // Implements expansion and contraction of the text input
  const onContentSizeChange = (
    e: NativeSyntheticEvent<{
      contentSize: { height: number; width: number };
    }>,
  ) => {
    const { contentSize } = e.nativeEvent;
    if (contentSize && contentSize.height > MIN_NOTE_TEXT_HEIGHT) {
      setTextInputSize(contentSize.height);
    } else if (contentSize && contentSize.height < MIN_NOTE_TEXT_HEIGHT) {
      setTextInputSize(MIN_NOTE_TEXT_HEIGHT);
    }
  };

  const onSaveNote = async () => {
    // if there is note reminder and the reminder date and time is in the past,
    // show an error message and do not save the note
    if (
      noteReminder &&
      noteReminder.time &&
      convertReminderToDate(noteReminder.date, noteReminder.time) < new Date()
    ) {
      // avoid showing multiple toasts if it is already visible
      if (!isToastVisible) {
        setIsToastVisible(true);
        showErrorToast(i18n.t('new_note_reminder.error_past_reminder'), () =>
          setIsToastVisible(false),
        );
      }
      return;
    }

    // if note is correctly saved, close the action sheet
    if (await createNote()) {
      onClose();
    }
  };

  const actionIconProps = {
    fill: theme.colors.transparent,
    stroke: theme.sk.color.icon.primary,
    width: 24,
  };

  return (
    <View
      style={[
        styles.sheet,
        {
          height: screenHeight * NOTE_TEXT_HEIGHT_PERCENT,
        },
      ]}
    >
      <View style={styles.noteHeaderContainer}>
        <View style={styles.noteHeader}>
          <Typography variant="heading-lg">
            {i18n.t('new_note.title')}
          </Typography>
          <Typography
            color={theme.sk.color.text.tertiary}
            variant="paragraph-md"
          >
            {`${formatDate(noteDate)} ${get12HourFormat(noteDate.getHours(), noteDate.getMinutes())}`}
          </Typography>
        </View>
        <View style={styles.noteHeaderActions}>
          {notesRemindersEnabled ? (
            <Button
              accessibilityLabel={i18n.t('new_note_reminder.action')}
              hoverStyle={styles.bellButtonHoverStyle}
              Icon={NotificationBellIcon}
              iconProps={actionIconProps}
              onPress={() => setShowReminderSheet(true)}
              ref={buttonRef}
              rounded
              size="small"
              style={[styles.actionButton, styles.bellButton]}
              trackingData={{ name: 'note_activity.open_reminder' }}
            />
          ) : null}
          {canListen ? (
            <Button
              Icon={MicIcon}
              iconProps={actionIconProps}
              onPress={isListening ? stopRecognition : startRecognition}
              rounded
              size="small"
              style={
                isListening ? styles.listeningSpeechButton : styles.actionButton
              }
              trackingData={{ name: 'note_activity.speech_to_text' }}
            />
          ) : null}
          {showCloseButton ? (
            <CloseButton
              Icon={DeleteThinIcon}
              iconProps={{
                fill: theme.sk.color.icon.primary,
                stroke: theme.colors.transparent,
                width: 16,
              }}
              onPress={onClose}
              trackingData={{ name: 'note_activity.close' }}
            />
          ) : null}
        </View>
      </View>

      <ScrollView>
        <TextInput
          accessibilityLabel="new_note.note_text_area"
          autoFocus
          containerStyle={{ height: textInputSize }}
          focusedBorderColor={theme.colors.white}
          inputContainerStyle={
            Platform.OS === 'android'
              ? {
                  height: textInputSize,
                }
              : null
          }
          inputRef={inputRef}
          multiline
          onChangeText={setNote}
          onContentSizeChange={onContentSizeChange}
          onSelectionChange={(event) => {
            cursorPositionRef.current = event.nativeEvent.selection.start;
          }}
          // kill the mic if someone taps into the text area
          onTouchEnd={stopRecognition}
          placeholder={
            Platform.OS === 'web'
              ? i18n.t('new_note.placeholder')
              : i18n.t('new_note.placeholder_with_speech')
          }
          style={[styles.textArea, { height: textInputSize }]}
          value={note}
        />
      </ScrollView>

      {noteReminder ? (
        <View style={styles.reminderBanner}>
          <MultiStyleText
            defaultStyle={[TYPOGRAPHY.p, styles.reminderBannerText]}
            emphasisStyle={[TYPOGRAPHY.pSemiBold, styles.reminderBannerText]}
          >
            {i18n.t('new_note_reminder.set_for', {
              date: formatDateWithDayAndMonth(noteReminder.date),
              time: noteReminder?.time
                ? get12HourFormat(
                    noteReminder.time.getHours(),
                    noteReminder.time.getMinutes(),
                  )
                : '',
            })}
          </MultiStyleText>
        </View>
      ) : null}

      <Button
        disabled={!note.trim()}
        loading={loading}
        onPress={onSaveNote}
        trackingData={{ name: 'note_activity.save_note' }}
      >
        {i18n.t('save')}
      </Button>

      {showReminderSheet ? (
        <AddNoteReminderActionSheet
          buttonRef={buttonRef}
          noteReminder={noteReminder}
          onChange={setNoteReminder}
          onClose={closeReminder}
        />
      ) : null}
    </View>
  );
};

export const NewNoteActionSheet: React.FC<Props> = ({ chatUUID, onClose }) => {
  const { isSmallLayout } = useLayoutContext();
  const isDesktop = !isSmallLayout && Platform.OS === 'web';

  return (
    <BaseActionSheet
      isVisible
      onClose={onClose}
      showCloseButton={false}
      toastPosition={Position.TOP}
    >
      <NewNoteActionSheetContent
        chatUUID={chatUUID}
        onClose={onClose}
        showCloseButton={isDesktop}
      />
    </BaseActionSheet>
  );
};

const useStyles = createStyles((theme) => ({
  actionButton: {
    backgroundColor: theme.sk.color.bg.tertiary.default,
  },
  bellButton: {
    marginRight: 16,
  },
  bellButtonHoverStyle: {
    opacity: 0.8,
  },
  listeningSpeechButton: {
    backgroundColor: theme.colors.accent,
  },
  noteHeader: {
    alignItems: 'flex-start',
  },
  noteHeaderActions: {
    alignItems: 'center',
    flexDirection: 'row',
  },
  noteHeaderContainer: {
    alignItems: 'center',
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
  reminderBanner: {
    backgroundColor: theme.sk.color.bg.secondary.default,
    borderRadius: 8,
    paddingHorizontal: 16,
    paddingVertical: 12,
  },
  reminderBannerText: {
    alignSelf: 'center',
    color: theme.sk.color.text.primary,
    fontSize: 14,
  },
  sheet: {
    gap: 16,
  },
  textArea: {
    ...theme.sk.typography.paragraph.base,
    fontWeight: 'normal',
    paddingLeft: 0,
    paddingRight: 16,
  },
}));
